import React, { useMemo, useCallback, useEffect } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import {
  Form,
  notification,
  DatePicker,
  Row,
  Input,
  Button as AntButton,
  Tag,
} from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import {
  MgBrokerVendor,
  createVendor,
  updateVendor,
  getVendorListByBrokerid,
  MgBrokerVendorResponse,
} from 'services/parametersService';
import DF from 'DisplayFramework';
import { useFormList } from 'hooks/useFormList';
import createEditableCell from 'utils/createEditableCell';
import {
  indexBy,
  toDatePickerDate,
  dateToString,
  OnlyIntegerNumber,
  setValueByNamePath,
} from 'utils/utils2';
import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import ActivateDisableVendorButton from '../Components/ActivateDisableVendor';
import ButtonOperation from 'components/ButtonOperation';
import UpdateButton from 'components/UpdateButtonAction';
import SaveButtonIcon from 'components/SaveBtn';
import MgUserListSelect from 'components/MgUserListSelect';

const NEW_ACTION_ID = -1;

export type BrokerVendorsTabProps = {
  mgid: number;
  tpidbroker: number;
  isCreate?: any;
  associdState?: boolean;
};

const BrokerVendorsTab = ({
  mgid,
  tpidbroker,
  isCreate,
  associdState,
}: BrokerVendorsTabProps) => {
  const [form] = Form.useForm();
  const fieldName = 'vendors';

  const { data, refetch } = useQuery<any>(
    ['getVendorListByBrokerid', { tpidbroker }],
    () =>
      getVendorListByBrokerid({ mgid, tpidbroker }).then(
        (res) => res.list || []
      ),
    { enabled: !!tpidbroker, refetchOnMount: true }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((vendor: MgBrokerVendor) => ({
          ...vendor,
          startdate: toDatePickerDate(vendor.startdate),
          enddate: toDatePickerDate(vendor.enddate),
        })),
      });
    }
  }, [form, data]);

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'vendorid',
    addAsFirst: true,
  });

  const getName = useCallback(
    (name: string) => (record: MgBrokerVendor) => {
      const vendors = form.getFieldValue(fieldName);
      const index = indexBy(vendors, 'vendorid')[record.vendorid];
      return [fieldName, index, name];
    },
    [form]
  );
  const onSave = useCallback(
    (record: MgBrokerVendor) => {
      const vendors: MgBrokerVendor[] = form.getFieldValue(fieldName);
      const index = indexBy(vendors, 'vendorid')[record.vendorid];
      const currentValue = vendors[index];
      const { mgid, vendorid } = currentValue;
      const vendorRequest = {
        ...record,
        startdate: dateToString(record.startdate),
        enddate: dateToString(record.enddate),
        mgid: mgid,

      };

      if (record.vendorname.length === 0) {
        return notification.error({
          message: 'name is mandatory',
        });
      } else if (record.vendorsurname.length === 0) {
        return notification.error({
          message: 'surname is mondatory',
        });
      } else if (record.vendortel.length === 0) {
        return notification.error({ message: 'Phone number is mondatory' });
      } else {
        vendorid === NEW_ACTION_ID
          ? createVendor({ mgid, vendorRequest }).then((response) => {
              if (response.code === '200') {
                notification.success({
                  message: response.message,
                });
                refetch();
              } else {
                notification.error({
                  message: response.message,
                  description: response.description,
                });
              }
            })
          : updateVendor({ vendorid, mgid, vendorRequest }).then((response) => {
              if (response.code === '200') {
                notification.success({
                  message: response.message,
                });
                refetch();
              } else {
                notification.error({
                  message: response.message,
                  description: response.description,
                });
              }
            });
      }
    },
    [form, refetch]
  );

  const columns = useMemo<DataGridColDef<MgBrokerVendorResponse>[]>(
    () => [
      // { dataIndex: 'vendorid', width: 100 },
      {
        dataIndex: 'userid',
        key: 'userid',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'userid',
            ]}
            noStyle
          >
            <MgUserListSelect
              mgid={mgid}
              onSelected={(e) => {
                

                setValueByNamePath(
                  form,
                  [
                    fieldName,
                    indexBy(form.getFieldValue(fieldName), 'vendorid')[
                      _record.vendorid
                    ],
                    'vendorname',
                  ],
                  e?.name ?? ''
                );
                setValueByNamePath(
                  form,
                  [
                    fieldName,
                    indexBy(form.getFieldValue(fieldName), 'vendorid')[
                      _record.vendorid
                    ],
                    'vendorsurname',
                  ],
                  e?.surname ?? ''
                );
                setValueByNamePath(
                  form,
                  [
                    fieldName,
                    indexBy(form.getFieldValue(fieldName), 'vendorid')[
                      _record.vendorid
                    ],
                    'vendoremail',
                  ],
                  e?.email ?? ''
                );
               
              }}
            />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'vendorname',
        key: 'vendorname',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'vendorname',
            ]}
            noStyle
          >
            <Input />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'vendorsurname',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'vendorsurname',
            ]}
            noStyle
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        ),
        // render: createEditableCell({
        //   getName: getName('vendorsurname'),
        // }),
        width: 140,
      },

      // {
      //   dataIndex: 'mgid',
      //   render: (mgid, record) => (
      //     <MgCompanyList
      //       // style={{ border: 'none', cursor: 'pointer !important' }}
      //       value={mgid}
      //     />
      //   ),
      //   width: 230,
      // },
      {
        dataIndex: 'vendoremail',
        render: createEditableCell({
          getName: getName('vendoremail'),
        }),
        width: 140,
      },
      {
        dataIndex: 'vendortel',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'vendortel',
            ]}
            noStyle
            normalize={OnlyIntegerNumber}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 170,
      },
      {
        dataIndex: 'vendormobile',
        // render: createEditableCell({
        //   getName: getName('vendormobile'),
        //   input: <InputNumber />
        // }),

        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'vendormobile',
            ]}
            noStyle
            normalize={OnlyIntegerNumber}
          >
            <Input min={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
        width: 170,
      },

      {
        dataIndex: 'startdate',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'startdate',
            ]}
            noStyle
          >
            <DatePicker value={moment(_record.startdate)} />
          </Form.Item>
        ),
        width: 140,
      },
      {
        dataIndex: 'enddate',
        render: (_value, _record, _index) => (
          <Form.Item
            name={[
              fieldName,
              indexBy(form.getFieldValue(fieldName), 'vendorid')[
                _record.vendorid
              ],
              'enddate',
            ]}
            noStyle
          >
            <DatePicker value={moment(_record.enddate)} />
          </Form.Item>
        ),
        width: 140,
      },
      {
        comcode: 'vendorstatus',
        dataIndex: 'vendorstatuslabel',
        render: (value, _record) => (
          <Tag
            color={
              _record.vendorstatus === 'ACTIVE'
                ? 'succes'
                : _record.vendorstatus === 'DISABLED'
                ? 'error'
                : 'default'
            }
          >
            {_record.vendorstatuslabel}
          </Tag>
        ),
        width: 100,
        fixed: 'right',
        align: 'center',
      },
      {
        key: 'actions',
        render: (_, record) =>
          record.vendorid === NEW_ACTION_ID ? (
            <Row justify="center">
              <UpdateButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(record);
                }}
                htmlType="button"
                comcode="save"
              />
              <AntButton
                danger
                onClick={() => {
                  remove(record.vendorid);
                }}
                htmlType="button"
                icon={
                  <CloseOutlined height="14px" width="14px" color="white" />
                }
              />
              {/* <Popconfirm
                title="Are you sure to delete this vendor?"
                onConfirm={() => {
                  remove(record.vendorid);
                }}
                okText="Yes"
                cancelText="No"
              >
                <DeleteButton onlyIcon />
              </Popconfirm> */}
            </Row>
          ) : (
            <Row justify="center">
              <ActivateDisableVendorButton
                mgid={record.mgid}
                vendorid={record.vendorid}
                statusToUpdate={record.vendorstatus}
                size="small"
                refetch={refetch}
              />
              <SaveButtonIcon
                onClick={(ev) => {
                  ev.preventDefault();
                  onSave(record);
                }}
                htmlType="button"
                onlyIcon
              />
            </Row>
          ),
        width: 210,
        fixed: 'right',
        align: 'center',
      },

      // {
      //   key: 'remove',
      //   render: (_, record) =>
      //    ,
      //   width: 100,
      // },
    ],
    [form, getName, onSave, refetch, remove]
  );

  const addRow = () => {
    const row: MgBrokerVendor = {
      vendorid: NEW_ACTION_ID,
      enddate: '',
      mgid,
      startdate: '',
      tpidbroker,
      userid: '' as any,
      vendoremail: '',
      vendormobile: '',
      vendorname: '',
      vendorstatus: 'INIT',
      vendorsurname: '',
      vendortel: '',
    };
    add(row);
  };

  return (
    <DF.Provider pagcode="brokerdetail">
      <Form form={form} initialValues={INITIAL_VALUES}>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const vendors: MgBrokerVendorResponse[] = getFieldValue(fieldName);
            const isNewItemInTable =
              vendors?.find(({ vendorid }) => vendorid === NEW_ACTION_ID) !==
              undefined;
            return (
              <DataGrid
                titleComcode="listOfVendors"
                rowKey="vendorid"
                dataSource={vendors}
                columns={columns}
                hideFilters
                extraActions={
                  <ButtonOperation
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={addRow}
                    disabled={isNewItemInTable || associdState === true}
                    comcode="add"
                  />
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </DF.Provider>
  );
};

const INITIAL_VALUES = {
  vendors: [],
};

export default BrokerVendorsTab;
